if (!customElements.get('cinoton-bundles')) {
  class CinotonBundles extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[data-bundle-tab]')];
      this.panels = [...this.querySelectorAll('[data-bundle-panel]')];
      this.viewport = this.querySelector('[data-bundle-viewport]');
      this.track = this.querySelector('[data-bundle-track]');
      this.current = 0;
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.activate(tab.dataset.bundleTab));
        tab.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          const next = (index + (event.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
          this.tabs[next].focus();
          this.activate(this.tabs[next].dataset.bundleTab);
        });
      });
      this.querySelector('[data-bundle-prev]')?.addEventListener('click', () => this.move(-1));
      this.querySelector('[data-bundle-next]')?.addEventListener('click', () => this.move(1));
      this.bindDrag();
      this.panels.forEach((panel) => {
        panel.querySelectorAll('[data-bundle-product]').forEach((input) => input.addEventListener('change', () => this.update(panel)));
        panel.querySelectorAll('[data-bundle-variant]').forEach((select) => select.addEventListener('change', () => this.update(panel)));
        panel.querySelector('[data-bundle-add]')?.addEventListener('click', () => this.addToCart(panel));
        this.update(panel);
      });
      this.addEventListener('shopify:block:select', (event) => {
        const panel = event.target.closest('[data-bundle-panel]');
        if (!panel) return;
        this.activate(panel.dataset.bundlePanel);
        window.requestAnimationFrame(() => {
          if (this.isConnected) this.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'nearest' });
        });
      });
      if (this.panels.length) this.activate(this.panels[0].dataset.bundlePanel);
    }

    activate(id) {
      const nextIndex = this.panels.findIndex((panel) => panel.dataset.bundlePanel === id);
      if (nextIndex < 0) return;
      this.current = nextIndex;
      this.tabs.forEach((tab) => {
        const active = tab.dataset.bundleTab === id;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
        if (active && tab.parentElement.scrollWidth > tab.parentElement.clientWidth) {
          tab.parentElement.scrollTo({ left: tab.offsetLeft - (tab.parentElement.clientWidth - tab.offsetWidth) / 2, behavior: 'smooth' });
        }
      });
      this.panels.forEach((panel) => {
        const active = panel.dataset.bundlePanel === id;
        panel.setAttribute('aria-hidden', String(!active));
        panel.toggleAttribute('inert', !active);
      });
      if (this.track) this.track.style.transform = `translate3d(${-100 * this.current}%, 0, 0)`;
      const counter = this.querySelector('[data-bundle-current]');
      if (counter) counter.textContent = String(this.current + 1).padStart(2, '0');
    }

    move(direction) {
      if (this.panels.length < 2) return;
      const next = (this.current + direction + this.panels.length) % this.panels.length;
      this.activate(this.panels[next].dataset.bundlePanel);
    }

    bindDrag() {
      if (!this.viewport || !this.track || this.panels.length < 2) return;
      const interactive = 'a, button, input, select, summary, details, label';
      let startX = 0;
      let deltaX = 0;
      let pointerId = null;

      this.viewport.addEventListener('pointerdown', (event) => {
        if (event.button !== 0 || event.target.closest(interactive)) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        deltaX = 0;
        this.viewport.classList.add('is-dragging');
        this.viewport.setPointerCapture(pointerId);
      });
      this.viewport.addEventListener('pointermove', (event) => {
        if (pointerId !== event.pointerId) return;
        deltaX = event.clientX - startX;
        this.track.style.transform = `translate3d(calc(${-100 * this.current}% + ${deltaX}px), 0, 0)`;
      });
      const finish = (event) => {
        if (pointerId !== event.pointerId) return;
        const threshold = Math.min(110, this.viewport.clientWidth * .14);
        this.viewport.classList.remove('is-dragging');
        if (Math.abs(deltaX) >= threshold) this.move(deltaX < 0 ? 1 : -1);
        else this.activate(this.panels[this.current].dataset.bundlePanel);
        pointerId = null;
      };
      this.viewport.addEventListener('pointerup', finish);
      this.viewport.addEventListener('pointercancel', finish);
    }

    selected(panel) {
      return [...panel.querySelectorAll('[data-bundle-product-row]')].filter((row) => row.querySelector('[data-bundle-product]:checked:not(:disabled)'));
    }

    update(panel) {
      const totalNode = panel.querySelector('[data-bundle-total]');
      const addButton = panel.querySelector('[data-bundle-add]');
      const message = panel.querySelector('[data-bundle-message]');
      if (!totalNode || !addButton || !message) return;
      const selected = this.selected(panel);
      const total = selected.reduce((sum, row) => sum + Number(row.querySelector('[data-bundle-variant]').selectedOptions[0]?.dataset.price || 0), 0);
      const currency = this.dataset.currency || 'USD';
      const format = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency });
      totalNode.textContent = format.format(total / 100);
      panel.querySelectorAll('[data-bundle-product-row]').forEach((row) => {
        const price = Number(row.querySelector('[data-bundle-variant]').selectedOptions[0]?.dataset.price || 0);
        row.querySelector('[data-bundle-line-price]').textContent = format.format(price / 100);
      });
      addButton.disabled = selected.length === 0;
      message.textContent = '';
    }

    async addToCart(panel) {
      const selected = this.selected(panel);
      if (!selected.length) return;
      const button = panel.querySelector('[data-bundle-add]');
      const message = panel.querySelector('[data-bundle-message]');
      button.disabled = true;
      message.textContent = '';
      try {
        const response = await fetch(this.dataset.addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: selected.map((row) => ({ id: Number(row.querySelector('[data-bundle-variant]').value), quantity: 1 })) })
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Could not add these products.');
        window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        message.textContent = error.message || 'Could not add these products.';
        button.disabled = false;
      }
    }
  }
  customElements.define('cinoton-bundles', CinotonBundles);
}
